import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PageEvent } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { PaymentMethod, PaymentMethodsService } from '../payment-methods.service';
import { PaymentMethodFormDialogComponent } from '../payment-method-form-dialog/payment-method-form-dialog.component';
import { HasPermissionDirective } from '../../../../../core/directives/has-permission.directive';
import { ConfirmationService } from '../../../../../shared/services/confirmation.service';
import { PageHeaderService } from '../../../../../shared/services/page-header.service';
import { SharedModule } from '../../../../../shared/shared.module';

@Component({
  selector: 'app-payment-methods-list',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatDialogModule,
    MatMenuModule,
    MatDividerModule,
    HasPermissionDirective,
    SharedModule,
  ],
  template: `
    <section class="config-page">
      <app-card variant="default" padding="none" class="config-table-card">
        <div *ngIf="loading" class="loading-container">
          <mat-progress-spinner mode="indeterminate" diameter="50"></mat-progress-spinner>
          <p>Loading payment methods...</p>
        </div>

        <div *ngIf="!loading && items.length === 0" class="empty-state">
          <div class="empty-state__icon"><mat-icon>payments</mat-icon></div>
          <h3>No payment methods yet</h3>
          <p>Add the payment methods you accept (cash, card, QR, bank).</p>
          <button mat-flat-button color="primary" (click)="onCreate()" *appHasPermission="'payment_method.create'">
            <mat-icon>add</mat-icon> Add Payment Method
          </button>
        </div>

        <div *ngIf="!loading && items.length > 0" class="table-container">
          <table mat-table [dataSource]="items" class="modern-table">
            <ng-container matColumnDef="name">
              <th mat-header-cell *matHeaderCellDef>Payment Method</th>
              <td mat-cell *matCellDef="let m">
                <div class="entity-cell">
                  <div class="entity-cell__icon"><mat-icon>{{ iconFor(m.type) }}</mat-icon></div>
                  <div class="entity-cell__details"><strong class="entity-cell__name">{{ m.name }}</strong></div>
                </div>
              </td>
            </ng-container>

            <ng-container matColumnDef="type">
              <th mat-header-cell *matHeaderCellDef>Type</th>
              <td mat-cell *matCellDef="let m"><span class="pill">{{ m.type }}</span></td>
            </ng-container>

            <ng-container matColumnDef="status">
              <th mat-header-cell *matHeaderCellDef>Status</th>
              <td mat-cell *matCellDef="let m">
                <span class="status-badge" [class.active]="m.is_active" [class.inactive]="!m.is_active">
                  <span class="status-indicator"></span>{{ m.is_active ? 'Active' : 'Inactive' }}
                </span>
              </td>
            </ng-container>

            <ng-container matColumnDef="actions">
              <th mat-header-cell *matHeaderCellDef>Actions</th>
              <td mat-cell *matCellDef="let m">
                <button mat-icon-button [matMenuTriggerFor]="menu" matTooltip="More actions" class="action-menu-trigger">
                  <mat-icon>more_vert</mat-icon>
                </button>
                <mat-menu #menu="matMenu" class="action-menu">
                  <button mat-menu-item (click)="onEdit(m)" *appHasPermission="'payment_method.update'">
                    <mat-icon>edit</mat-icon><span>Edit</span>
                  </button>
                  <button mat-menu-item (click)="onToggle(m)" *appHasPermission="'payment_method.update'">
                    <mat-icon>{{ m.is_active ? 'toggle_off' : 'toggle_on' }}</mat-icon>
                    <span>{{ m.is_active ? 'Deactivate' : 'Activate' }}</span>
                  </button>
                  <mat-divider></mat-divider>
                  <button mat-menu-item (click)="onDelete(m)" class="danger-action" *appHasPermission="'payment_method.delete'">
                    <mat-icon>delete</mat-icon><span>Delete</span>
                  </button>
                </mat-menu>
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
          </table>

          <mat-paginator
            [length]="total"
            [pageSize]="pageSize"
            [pageIndex]="pageIndex"
            [pageSizeOptions]="pageSizeOptions"
            [showFirstLastButtons]="true"
            aria-label="Payment method pagination"
            (page)="handlePageEvent($event)"
          ></mat-paginator>
        </div>
      </app-card>
    </section>
  `,
  styleUrls: ['../../config-list.shared.scss'],
})
export class PaymentMethodsListComponent implements OnInit, OnDestroy {
  items: PaymentMethod[] = [];
  loading = false;
  displayedColumns = ['name', 'type', 'status', 'actions'];

  total = 0;
  pageIndex = 0;
  pageSize = 25;
  readonly pageSizeOptions = [10, 25, 50, 100];

  constructor(
    private service: PaymentMethodsService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private confirmationService: ConfirmationService,
    private pageHeader: PageHeaderService,
  ) {}

  ngOnInit(): void {
    this.pageHeader.set({
      title: 'Payment Methods',
      icon: 'payments',
      backRoute: '/dashboard',
      actions: [
        { label: 'Refresh', icon: 'refresh', variant: 'stroked', permission: 'payment_method.read', disabled: () => this.loading, handler: () => this.load() },
        { label: 'Add Payment Method', icon: 'add', variant: 'primary', permission: 'payment_method.create', handler: () => this.onCreate() },
      ],
    });
    this.load();
  }

  ngOnDestroy(): void {
    this.pageHeader.clear();
  }

  iconFor(type: string): string {
    switch (type) {
      case 'cash': return 'payments';
      case 'card': return 'credit_card';
      case 'qr': return 'qr_code_2';
      case 'bank': return 'account_balance';
      default: return 'payments';
    }
  }

  load(): void {
    this.loading = true;
    this.service.getPage({ page: this.pageIndex + 1, perPage: this.pageSize }).subscribe({
      next: (page) => {
        this.items = page.items;
        this.total = page.meta.total;
        this.pageSize = page.meta.perPage;
        this.pageIndex = Math.max(page.meta.currentPage - 1, 0);
        this.loading = false;
      },
      error: () => {
        this.snackBar.open('Failed to load payment methods', 'Close', { duration: 3000 });
        this.loading = false;
      },
    });
  }

  handlePageEvent(event: PageEvent): void {
    if (this.pageSize === event.pageSize && this.pageIndex === event.pageIndex) {
      return;
    }
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.load();
  }

  onCreate(): void {
    this.dialog.open(PaymentMethodFormDialogComponent, { width: '560px', maxHeight: '90vh', data: {} })
      .afterClosed().subscribe((saved) => { if (saved) this.load(); });
  }

  onEdit(m: PaymentMethod): void {
    this.dialog.open(PaymentMethodFormDialogComponent, { width: '560px', maxHeight: '90vh', data: { id: m.id } })
      .afterClosed().subscribe((saved) => { if (saved) this.load(); });
  }

  onToggle(m: PaymentMethod): void {
    this.service.toggleActive(m.id).subscribe({
      next: (res) => {
        if (res.success) {
          m.is_active = !m.is_active;
          this.snackBar.open(`Payment method ${m.is_active ? 'activated' : 'deactivated'}`, 'Close', { duration: 3000 });
        }
      },
      error: () => this.snackBar.open('Failed to update status', 'Close', { duration: 3000 }),
    });
  }

  onDelete(m: PaymentMethod): void {
    this.confirmationService.confirm({
      title: 'Delete payment method',
      message: `Are you sure you want to delete "${m.name}"?`,
      confirmText: 'Delete',
      type: 'danger',
    }).subscribe((confirmed) => {
      if (!confirmed) return;
      this.service.delete(m.id).subscribe({
        next: (res) => {
          if (res.success) {
            this.snackBar.open('Payment method deleted', 'Close', { duration: 3000 });
            this.load();
          }
        },
        error: (error) => this.snackBar.open(error.error?.message || 'Failed to delete', 'Close', { duration: 5000 }),
      });
    });
  }
}
